

/**
 * Resolves a potentially relative URL against the document's <base> tag or current window location.
 *
 * @param {string} urlString - A relative or absolute URL string.
 * @returns {string|null} A fully qualified URL string, or null if resolution fails.
 */
export function resolveRelativeUrl(urlString) {
  let url;
  try {
    const base = document.querySelector("base")?.href || window.location.href;
    url = new URL(urlString, base);
  } catch (error) {
    console.error("[url.js] resolveRelativeUrl() Invalid url:", urlString, error);
    return null;
  }
  return url.toString();
}


// Extract parameters from the window.location
export function getWindowLocationParams() {
    var urlParams = {};

    var e,
        a = /\+/g,  // Regex for replacing addition symbol with a space
        r = /([^&=]+)=?([^&]*)/g,
        d = function (s) { return decodeURIComponent(s.replace(a, " ")); },
        q = window.location.search.substring(1);

    while (e = r.exec(q))
        urlParams[d(e[1])] = d(e[2]);

    return urlParams
}

// Use the DOM to extract the components of a URL.
// eg. getLocation('http://www.example.com/foo/bar.html').pathname //=> '/foo/bar.html'
// https://stackoverflow.com/a/736970/19588
export function getLocation(href) {
    var l = document.createElement("a");
    l.href = href;
    return l;
};


// This function is a bit hacky as we manually merge the params with the url (which may already have search params present).
// It would be nice if the `qs` package allowed for stringify to an array with each element a key/value pair.
// Here I'm resorting to stringify and then splitting by '&' and '='.
export function appendParamsToUrl(url, params) {
    params = paramsAsPairs(params);
    if (!params || !url) return null;

    // Parse the endpoint into a URL so we can access and append to `searchParams`.
    url = urlAsUrlInstance(url);

    // Merge in our `params` to the url's `searchParams` property.
    // Note that this also encodes chars.
    params.forEach((param) => {
        url.searchParams.append(param[0], param[1]);
    });

    return url;
}

import {stringify} from "qs";
// Stringify the `params` arg so that the query string keys are correct for nested params (using square bracket notation).
// For example `person[email]=foo@example.com`.
// Split them into an array of arrays. Each a key/value pair so we can append them to a URL `searchParams` property.
// @returns {array} An array of key/value pairs (as an array) eg. `[['foo','bar'], ['baz','bar']]`.
export function paramsAsPairs(params) {
    return stringify(params, { encode: false }).split('&').map((param) => {
        const key = (param.split('=')[0]);
        const value = (param.split('=')[1]);
        return [key, value];
    });
}

export function valueFromUrlParams(key, valueType = 'string') {
    const rawValue = new URLSearchParams(window.location.search).get(key);
    return parseValueByType(rawValue, valueType);
}

/**
 * Extract a parameter value from a URL string.
 * Similar to valueFromUrlParams but works with any URL, not just window.location.
 */
export function valueFromUrl(urlString, key, valueType = 'string') {
    try {
        const url = urlAsUrlInstance(urlString);
        const rawValue = url.searchParams.get(key);
        return parseValueByType(rawValue, valueType);
    } catch (e) {
        console.warn("[url.js] valueFromUrl() could not parse URL:", urlString, e);
        return null;
    }
}

function parseValueByType(rawValue, valueType) {
    if (rawValue === null || rawValue === undefined) return null;

    if (valueType === 'boolean') {
        return (rawValue == 'true' || rawValue == '1');
    }

    if (valueType === 'integer') {
        const parsedValue = parseInt(rawValue);
        if (isNaN(parsedValue)) return null;
        return parsedValue;
    }

    if (valueType === 'float') {
        const parsedValue = parseFloat(rawValue);
        if (isNaN(parsedValue)) return null;
        return parsedValue;
    }

    return rawValue;
}


export function urlAsUrlInstance(url) {
    try {
        url = new URL(url);
    } catch (TypeError) { // Generally because the `url` is a relative url and a URL instance needs a base set.
        url = new URL(url, `${window.location.protocol}//${window.location.host}`);
    }
    return url;
}